import { Link } from "react-router-dom";
import { Clock, ArrowUpRight } from "lucide-react";
import { CATEGORY_COLOR, categoryGradient } from "@/lib/blogApi";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return ""; }
};

export const BlogCard = ({ post, featured = false }) => {
  const color = CATEGORY_COLOR[post.category] || CATEGORY_COLOR["Market Intelligence"];

  if (featured) {
    return (
      <Link
        to={`/journal/${post.slug}`}
        data-testid={`blog-featured-${post.slug}`}
        className="group grid grid-cols-1 lg:grid-cols-12 bg-white border border-brand-green/15 hover:border-brand-gold hover:shadow-[0_20px_60px_-20px_rgba(0,109,119,0.25)] transition-all duration-300"
      >
        <div
          className="lg:col-span-5 aspect-[4/3] lg:aspect-auto relative overflow-hidden"
          style={{ background: categoryGradient(post.category) }}
        >
          <div className="absolute inset-0 grain opacity-20 mix-blend-overlay" />
          <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
            <span
              className="font-mono text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 backdrop-blur bg-black/20"
              style={{ color: color.text }}
            >
              {post.category}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: color.text, opacity: 0.7 }}>
              Featured
            </span>
          </div>
          <div className="absolute bottom-6 left-6 font-display text-5xl" style={{ color: color.text, opacity: 0.85 }}>
            ✦
          </div>
        </div>
        <div className="lg:col-span-7 p-8 md:p-12 flex flex-col">
          <p className="eyebrow mb-4">◦  Featured Article</p>
          <h2 className="font-display text-3xl md:text-4xl text-brand-black tracking-tight leading-tight group-hover:text-brand-teal transition">
            {post.title}
          </h2>
          <p className="text-brand-black/70 mt-4 leading-relaxed">{post.excerpt}</p>
          <div className="mt-auto pt-8 flex items-center justify-between border-t border-brand-green/10">
            <div className="flex items-center gap-3 text-xs text-brand-black/60">
              <span className="font-mono">{post.author?.initials || ""}</span>
              <span>·</span>
              <span>{post.author?.name}</span>
              <span>·</span>
              <span>{fmtDate(post.publishedAt)}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Clock size={12} />{post.readingTime} min</span>
            </div>
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-brand-green group-hover:text-brand-gold transition">
              Read Article <ArrowUpRight size={14} />
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/journal/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group bg-white border border-brand-green/15 hover:border-brand-gold hover:shadow-[0_18px_40px_-15px_rgba(0,109,119,0.22)] hover:-translate-y-1 transition-all duration-300 flex flex-col h-full overflow-hidden"
    >
      <div
        className="aspect-[16/9] relative overflow-hidden"
        style={{ background: categoryGradient(post.category) }}
      >
        <div className="absolute inset-0 grain opacity-20 mix-blend-overlay" />
        <div className="absolute top-4 left-4">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 backdrop-blur bg-black/20"
            style={{ color: color.text }}
          >
            {post.category}
          </span>
        </div>
        <div className="absolute bottom-3 right-4 font-display text-3xl" style={{ color: color.text, opacity: 0.7 }}>
          ✦
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="font-display text-xl text-brand-black leading-snug line-clamp-2 group-hover:text-brand-teal transition">
          {post.title}
        </h3>
        <p className="text-sm text-brand-black/65 mt-3 leading-relaxed line-clamp-3">{post.excerpt}</p>
        <div className="mt-auto pt-5 flex items-center justify-between text-[11px] text-brand-black/55 border-t border-brand-green/10">
          <span className="font-mono">{post.author?.initials || ""} · {post.author?.name}</span>
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {post.readingTime} min</span>
        </div>
      </div>
    </Link>
  );
};

export default BlogCard;
